import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, X, Check } from "lucide-react";
import api from "../../lib/api";

/* Spec §10 — One-tap template picker on the trip page. */

export default function PrepTemplatePicker({ tripId, onApplied }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [applied, setApplied] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    api.get("/prep-templates/list").then((r) => setTemplates(r.data.templates || []))
      .catch(() => setTemplates([]));
    api.get(`/prep-templates/trips/${tripId}/tasks`).then((r) => {
      const ids = new Set((r.data.items || []).map((t) => t.template_id));
      setApplied(ids);
    }).catch(() => {});
  }, [open, tripId]);

  const apply = async (id) => {
    try {
      const { data } = await api.post("/prep-templates/apply", { trip_id: tripId, template_id: id });
      if (data.applied) toast.success(`Applied ${data.template}`);
      else toast.message("Already applied to this trip");
      setApplied((s) => new Set([...s, id]));
      onApplied && onApplied();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
        data-testid="prep-template-btn"
        className="w-full rounded-2xl border-2 border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100 hover:border-blue-500 transition py-4 px-5 flex items-center gap-3">
        <Sparkles className="w-5 h-5 text-blue-600" />
        <div className="text-left flex-1">
          <div className="font-serif font-800 text-blue-900">Prepare with a template</div>
          <div className="text-xs text-blue-700/80">Pick from 14 curated checklists — Cruise, Family, Pilgrimage, Wedding…</div>
        </div>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)} data-testid="prep-template-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
            <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex items-start justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-slate-500">Spec §10</div>
                <div className="font-serif font-800 text-xl text-blue-900 mt-0.5">Choose a template</div>
              </div>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full grid place-items-center text-slate-400 hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="overflow-y-auto flex-1 p-4 grid sm:grid-cols-2 gap-2.5">
              {templates.map((t) => {
                const isApplied = applied.has(t.id);
                return (
                  <button key={t.id} onClick={() => apply(t.id)} disabled={isApplied}
                    data-testid={`template-${t.id}`}
                    className={`text-left rounded-xl border p-3.5 transition ${isApplied ? "border-emerald-200 bg-emerald-50" : "border-slate-200 hover:border-blue-400 hover:bg-blue-50"}`}>
                    <div className="flex items-start gap-2.5">
                      <span className="text-2xl leading-none shrink-0">{t.emoji}</span>
                      <div className="min-w-0 flex-1">
                        <div className="font-serif font-800 text-slate-900 flex items-center gap-1.5">
                          {t.label}
                          {isApplied && <Check className="w-4 h-4 text-emerald-600" />}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>
                        <div className="text-[10px] font-800 uppercase tracking-wider text-blue-600 mt-1.5">{t.task_count} tasks</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
